/** @jsx jsx */

import { Fragment as _, memo, useContext } from 'react';
import { css, jsx } from '@emotion/core';
import EditableText from 'components/lib/EditableText';
import ClickAndReveals from './ClickAndReveals';
import ClickAndReveal from './ClickAndReveal';
import ClickAndRevealContent from './ClickAndRevealContent';
import renderBlock from 'services/apiHelpers/renderBlock';
import {
  Row,
  Column,
  SingleColumnLayout,
  TwoColumnLayout,
} from 'components/UI/ColumnAndRow';
import Image from 'components/lib/Image';
import Video from 'components/lib/Video';
import Audio from 'components/lib/Audio';
import SVG from 'components/lib/SVG';
import Media, { MediaContext, IMediaContext } from 'components/lib/Media';
import { imgPlaceholders } from 'components/lib/Image';
import clickToRevealPanelPlaceholder from 'assets/Square.svg';
import { interactionContext as InteractionContext } from './interactionContext';
import Container from 'components/Layouts/ContainerLogical';
import getBlockById from 'services/apiHelpers/getBlockById';
import continueBtn from '../ContinueBtn/continueBtn_v2';
import { Block } from 'factories/block';
import EditableBtn from 'components/lib/EditableBtn';
import getMediaParent from 'services/apiHelpers/getMediaParent';
import shouldUpdate from 'services/shouldUpdate';
import imagePlaceholder from 'assets/clickToRevealMagicMan.png';
import longRectangle from 'assets/Long Rectangle.svg';
import Embed from 'components/lib/Embed';
import PDF from 'components/lib/PDF';
import { OpenPDF } from 'components/lib/PDF/OpenPDF';
import { ImageContext } from 'components/lib/Image';
import modalPlaceholder from 'assets/Long Rectangle.svg';
import Mid_Rectangle from 'assets/Mid Rectangle.svg';
import Short_Rectangle from 'assets/Short Rectangle.svg';

const Components = {
  Row,
  Column,
  Text: ({ children, ...props }) => (
    <EditableText {...{ ...props, className: 'click-and-reveal-text' }} />
  ),
  InteractionColumn: ({ children, ...props }) => {
    const interactionContext = useContext(InteractionContext);
    const { includeAdditionalTextField } = interactionContext.props;

    const [clickAndReveals, txt] = children;

    if (!includeAdditionalTextField) {
      return clickAndReveals;
    }

    return children;
  },
  ClickAndReveals,
  ClickAndReveal({ children, ...props }) {
    const interactionContext = useContext(InteractionContext);

    const { isLandingPage } = interactionContext?.props;

    return (
      <ImageContext.Provider
        value={{
          editWrapperType: 1,
          editWrapperSize: '65%',
          editWrapperUseIcon: false,
          placeholder: isLandingPage
            ? Short_Rectangle || Mid_Rectangle
            : clickToRevealPanelPlaceholder || imagePlaceholder,
          //
          canUpdateContainerHeight: false,
        }}
      >
        <ClickAndReveal {...props}>{children}</ClickAndReveal>
      </ImageContext.Provider>
    );
  },

  Feedback: ({ children, ...props }) => children,
  Content: ({ children, ...props }) => {
    const { layout = '' } = props;

    return (
      <span
        css={css({
          width: '100%',
          height: '100%',
          '[class*="OpenPDF"]': {
            ...(layout.toLowerCase().includes('one') && {
              img: {
                maxHeight: '50vh !important',
              },
            }),
          },
        })}
      >
        <ImageContext.Provider
          value={{
            editWrapperType: 0,
            editWrapperSize: '100%',
            placeholder: modalPlaceholder,
            padding: 5,
          }}
        >
          <ClickAndRevealContent {...props}>{children}</ClickAndRevealContent>
        </ImageContext.Provider>
      </span>
    );
  },
  ColumnRowA: ({ children, ...props }) => children,
  ColumnRowB: ({ children, ...props }) => children,
  Image: ({ children, ...props }) => {
    const mediaContext = useContext(MediaContext) as IMediaContext;
    const { isAdditionalMedia } = mediaContext;

    const mediaParent = getMediaParent(props.id);

    return mediaParent ? (
      <Image
        {...{
          ...props,
          placeholder: longRectangle || imgPlaceholders[1],
        }}
      />
    ) : (
      <Image {...{ ...props, usePlaceholderValue: true }} />
    );
  },
  Video,
  Audio,
  Transcript: () => null,
  SVG: ({ children, ...props }) => (
    <SVG {...{ ...props, editWrapperType: 1 }} />
  ),
  Media,
  Embed,
  PDF,
  OpenPDF: ({ children, ...props }) => {
    return <OpenPDF {...(props as any)}>{children}</OpenPDF>;
  },
  TwoColumnLayout,
  SingleColumnLayout,
  ContinueButton: continueBtn,
  EditableButton: EditableBtn,
};

const ClickAndRevealDisplay = memo((props: any) => {
  const interactionBlock = getBlockById(props.blockId);
  return (
    <Container
      id={interactionBlock.id}
      key={interactionBlock.id}
      {...interactionBlock.props}
      {...props}
    >
      <InteractionContext.Provider
        value={{ ...interactionBlock, updateItemsSeen: null }}
      >
        {(interactionBlock.children as Block[]).map((child) => {
          return renderBlock(child, Components);
        })}
      </InteractionContext.Provider>
    </Container>
  );
}, shouldUpdate as any);

export default ClickAndRevealDisplay;
